import { useEffect, useRef, useState } from 'react';
import { MdFitnessCenter } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import { useAuth } from '../../../context/AuthContext';
import { changeOwnPassword } from '../../../services/auth/authApi';
import { useGymSettings } from '../../../context/GymSettingsContext';
import { usePanelToast } from '../../../context/PanelToastContext';
import { compressImageFile } from '../../../utils/compressImage';
import {
  currencyOptions,
  timezoneOptions,
  getAdminSignupEmail,
  defaultSettings,
} from './settingsData';
import { setInvoiceSequenceStart } from '../../../services/receptionist/invoiceSequence';
import PasswordField from '../../auth/PasswordField';
import '../../owner/ownerPages.css';
import '../../owner/settings/settings.css';
import './adminSettings.css';

function AdminSettings() {
  const fileInputRef = useRef(null);
  const toast = usePanelToast();
  const { account, user } = useAuth();
  const {
    settings: savedSettings,
    saveSettings,
    applySettings,
    loading: settingsLoading,
    gymId,
  } = useGymSettings();

  const [settings, setSettings] = useState(savedSettings || defaultSettings);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [error, setError] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const adminEmail = getAdminSignupEmail(gymId || account?.gym_id) || account?.email || '';
  const loginEmail = account?.email || user?.email || adminEmail;

  useEffect(() => {
    if (savedSettings) {
      setSettings((prev) => ({
        ...defaultSettings,
        ...savedSettings,
        gym: { ...defaultSettings.gym, ...savedSettings.gym },
        currency: { ...defaultSettings.currency, ...savedSettings.currency },
        invoiceSequenceStart: Number(savedSettings.invoiceSequenceStart) > 0
          ? Math.floor(Number(savedSettings.invoiceSequenceStart))
          : defaultSettings.invoiceSequenceStart,
        // Don't wipe a logo the user just uploaded if refresh briefly returns without it
        logoPreview: savedSettings.logoPreview || prev.logoPreview || null,
      }));
    }
  }, [savedSettings]);

  const persistLogo = async (nextSettings) => {
    const id = gymId || account?.gym_id;
    if (!id) throw new Error('Gym context is missing. Sign out and sign in again.');
    const saved = await saveSettings(nextSettings);
    applySettings(saved);
    return saved;
  };

  const updateGym = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, gym: { ...prev.gym, [name]: value } }));
    setError('');
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    if (name === 'code') {
      const selected = currencyOptions.find((c) => c.code === value);
      setSettings((prev) => ({
        ...prev,
        currency: {
          ...prev.currency,
          code: value,
          symbol: selected?.symbol || prev.currency.symbol,
        },
      }));
    } else {
      setSettings((prev) => ({
        ...prev,
        currency: {
          ...prev.currency,
          [name]: name === 'decimalPlaces' ? Number(value) : value,
        },
      }));
    }
    setError('');
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    try {
      setLogoBusy(true);
      setError('');
      const dataUrl = await compressImageFile(file);
      const next = { ...settings, logo: null, logoPreview: dataUrl };
      setSettings(next);
      await persistLogo(next);
      toast.showSaved('Logo');
    } catch (err) {
      const message = err.message || 'Failed to upload logo';
      setError(message);
      toast.showError(message);
    } finally {
      setLogoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setLogoBusy(true);
      setError('');
      const next = { ...settings, logo: null, logoPreview: null };
      setSettings(next);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await persistLogo(next);
      toast.showDeleted('Logo');
    } catch (err) {
      const message = err.message || 'Failed to delete logo';
      setError(message);
      toast.showError(message);
    } finally {
      setLogoBusy(false);
    }
  };

  const handleSave = async () => {
    if (!gymId && !account?.gym_id) {
      setError('Gym context is missing. Sign out and sign in again.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const crStart = Math.floor(Number(settings.invoiceSequenceStart) || 0);
      if (!Number.isFinite(crStart) || crStart < 1) {
        throw new Error('Enter a valid Next CR. No. (1 or higher).');
      }
      const next = { ...settings, invoiceSequenceStart: crStart };
      const gid = gymId || account?.gym_id;
      setInvoiceSequenceStart(crStart, gid);
      await saveSettings(next);
      toast.showSaved('Settings');
    } catch (err) {
      const message = err.message || 'Failed to save settings';
      setError(message);
      toast.showError(message);
    } finally {
      setSaving(false);
    }
  };

  const updatePasswordField = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setPasswordError('');
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    try {
      setPasswordSaving(true);
      setPasswordError('');
      await changeOwnPassword({
        email: loginEmail,
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.showSaved('Password');
    } catch (err) {
      const message = err.message || 'Failed to change password';
      setPasswordError(message);
      toast.showError(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  const formatPreview = (amount = 28000) => {
    const decimals = Number(settings.currency.decimalPlaces ?? 0);
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return settings.currency.position === 'before'
      ? `${settings.currency.symbol} ${formatted}`
      : `${formatted} ${settings.currency.symbol}`;
  };

  return (
    <div className="admin-settings-page">
      <PageHeader
        title="Settings"
        subtitle="Gym profile and currency for this admin panel and its receptionist panel"
      />

      {settingsLoading && <p className="owner-placeholder-text owner-mb-16">Loading settings...</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="owner-card settings-section">
        <h2 className="owner-card-title">Gym Information</h2>
        <div className="settings-form-grid">
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="gym-name">Gym Name</label>
            <input
              id="gym-name"
              name="name"
              className="owner-form-input"
              value={settings.gym.name}
              onChange={updateGym}
              disabled={settingsLoading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="gym-tagline">Tagline</label>
            <input
              id="gym-tagline"
              name="tagline"
              className="owner-form-input"
              value={settings.gym.tagline}
              onChange={updateGym}
              disabled={settingsLoading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              type="email"
              className="owner-form-input admin-settings-readonly"
              value={adminEmail}
              readOnly
              aria-readonly="true"
            />
            <p className="admin-settings-field-hint">Set during admin sign-up and cannot be changed here.</p>
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="gym-phone">Phone</label>
            <input
              id="gym-phone"
              name="phone"
              type="tel"
              className="owner-form-input"
              value={settings.gym.phone}
              onChange={updateGym}
              disabled={settingsLoading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="gym-website">Website</label>
            <input
              id="gym-website"
              name="website"
              className="owner-form-input"
              value={settings.gym.website}
              onChange={updateGym}
              disabled={settingsLoading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="gym-hours">Opening Hours</label>
            <input
              id="gym-hours"
              name="openingHours"
              className="owner-form-input"
              value={settings.gym.openingHours}
              onChange={updateGym}
              disabled={settingsLoading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="gym-timezone">Timezone</label>
            <select
              id="gym-timezone"
              name="timezone"
              className="owner-form-select"
              value={settings.gym.timezone}
              onChange={updateGym}
              disabled={settingsLoading || saving}
            >
              {timezoneOptions.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div className="owner-form-group settings-form-full">
            <label className="owner-form-label" htmlFor="gym-address">Address</label>
            <textarea
              id="gym-address"
              name="address"
              className="owner-form-input owner-form-textarea"
              rows={3}
              value={settings.gym.address}
              onChange={updateGym}
              disabled={settingsLoading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="invoice-sequence-start">Next CR. No. (Invoice ID)</label>
            <input
              id="invoice-sequence-start"
              type="number"
              min="1"
              step="1"
              className="owner-form-input"
              value={settings.invoiceSequenceStart ?? ''}
              onChange={(e) => {
                setSettings((prev) => ({
                  ...prev,
                  invoiceSequenceStart: e.target.value,
                }));
                setError('');
              }}
              disabled={settingsLoading || saving}
            />
            <p className="admin-settings-field-hint">
              Next digital receipt uses this CR. No. / Invoice ID (then +1). Default 36128.
            </p>
          </div>
        </div>
      </div>

      <div className="owner-card settings-section">
        <h2 className="owner-card-title">Logo</h2>
        <div className="settings-logo-upload">
          <div className="settings-logo-preview">
            {settings.logoPreview ? (
              <img src={settings.logoPreview} alt="Gym logo preview" />
            ) : (
              <span className="settings-logo-placeholder"><MdFitnessCenter /></span>
            )}
          </div>
          <div className="settings-logo-actions">
            <input
              ref={fileInputRef}
              id="logo-upload"
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="settings-file-input"
              onChange={handleLogoSelect}
            />
            {settings.logoPreview ? (
              <button
                type="button"
                className="owner-btn owner-btn-secondary"
                onClick={handleDeleteLogo}
                disabled={settingsLoading || saving || logoBusy}
              >
                {logoBusy ? 'Deleting…' : 'Delete'}
              </button>
            ) : (
              <button
                type="button"
                className="owner-btn owner-btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={settingsLoading || saving || logoBusy}
              >
                {logoBusy ? 'Uploading…' : 'Upload Logo'}
              </button>
            )}
            <p className="settings-logo-hint">PNG, JPG, SVG or WebP. Shown on print and export documents.</p>
          </div>
        </div>
      </div>

      <div className="owner-card settings-section">
        <h2 className="owner-card-title">Currency</h2>
        <div className="settings-form-grid">
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="currency-code">Currency</label>
            <select
              id="currency-code"
              name="code"
              className="owner-form-select"
              value={settings.currency.code}
              onChange={handleCurrencyChange}
              disabled={settingsLoading || saving}
            >
              {currencyOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="currency-symbol">Currency Symbol</label>
            <input
              id="currency-symbol"
              name="symbol"
              className="owner-form-input"
              value={settings.currency.symbol}
              onChange={handleCurrencyChange}
              disabled={settingsLoading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="currency-position">Symbol Position</label>
            <select
              id="currency-position"
              name="position"
              className="owner-form-select"
              value={settings.currency.position}
              onChange={handleCurrencyChange}
              disabled={settingsLoading || saving}
            >
              <option value="before">Before amount (Rs 1,000)</option>
              <option value="after">After amount (1,000 Rs)</option>
            </select>
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="currency-decimals">Decimal Places</label>
            <select
              id="currency-decimals"
              name="decimalPlaces"
              className="owner-form-select"
              value={settings.currency.decimalPlaces}
              onChange={handleCurrencyChange}
              disabled={settingsLoading || saving}
            >
              <option value={0}>0</option>
              <option value={2}>2</option>
            </select>
          </div>
          <div className="owner-form-group settings-form-full">
            <div className="settings-currency-preview">
              <div className="settings-currency-preview-label">Preview</div>
              <div className="settings-currency-preview-value">{formatPreview()}</div>
            </div>
            <p className="admin-settings-field-hint">
              This currency is used across the gym admin panel and the receptionist panel for this gym.
            </p>
          </div>
        </div>

        <div className="settings-save-bar">
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={handleSave}
            disabled={settingsLoading || saving}
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="owner-card settings-section">
        <h2 className="owner-card-title">Change password</h2>
        <p className="admin-settings-field-hint owner-mb-16">
          Updates the password you use to sign in as {loginEmail || 'this admin'}.
        </p>
        {passwordError && (
          <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
            {passwordError}
          </p>
        )}
        <div className="settings-form-grid">
          <PasswordField
            id="admin-current-password"
            name="currentPassword"
            label="Current password"
            value={passwordForm.currentPassword}
            onChange={updatePasswordField}
            autoComplete="current-password"
            disabled={passwordSaving}
          />
          <PasswordField
            id="admin-new-password"
            name="newPassword"
            label="New password"
            value={passwordForm.newPassword}
            onChange={updatePasswordField}
            autoComplete="new-password"
            minLength={6}
            disabled={passwordSaving}
          />
          <PasswordField
            id="admin-confirm-password"
            name="confirmPassword"
            label="Confirm new password"
            value={passwordForm.confirmPassword}
            onChange={updatePasswordField}
            autoComplete="new-password"
            minLength={6}
            disabled={passwordSaving}
          />
        </div>
        <div className="settings-save-bar">
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={handleChangePassword}
            disabled={passwordSaving || !passwordForm.currentPassword || !passwordForm.newPassword}
          >
            {passwordSaving ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;
